import * as React from 'react'
import { FilterPanel, useEffectWithPolling, WidgetWrapper } from 'uxp/components'
import { IUIPropertyDefinitionMap, IWidgetTemplateInstanceProps, IWidgetTemplateProps, registerCardTemplate } from '../../../common/common'
import { generateIconProp } from '../../../common/icon-selector/IconEditor'
import { SelectableItem, selectUIProps } from '../../../common/SelectableItem'
import { ContainerProperty, extractContainerProperties } from '../../../common/ui-props/ContainerProperty'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TextFieldProperty } from '../../../common/ui-props/TextFieldProperty'
import { SimpleTextProperty } from '../../../common/ui-props/SimpleTextProperty'
import { IBoxProps, IGridLayoutWidgetProps, GridLayoutWidgetProperty, NumberInput, IGridLayoutCommonProps, GridLayoutCommonProps } from './GridLayoutWidgetProperty'
import { formatNumber, hasValue } from '../../../../util'
import { executeAndBind, processInitialSource } from '../../../common/data-source-picker/datasource'
import { IContextData, useInitialContextData } from '../../../../hooks/useContextData'
import _ from 'lodash'
import './styles.scss'

type BoxesValues = Array<{ [key: string]: any }>

const XPR: React.ForwardRefExoticComponent<React.RefAttributes<IWidgetTemplateInstanceProps> & IWidgetTemplateProps> = React.forwardRef((props, ref) => {
    const uiProps = props?.uiProps || {};

    const defaultCommonFieldProps: IGridLayoutCommonProps = {
        orientation: 'Row',
        borderColor: '#424242',
        showBorder: true
    }

    const defaultFieldProps: IGridLayoutWidgetProps = {
        boxProps: [
            {
                label: 'Max Ocuupancy',
                labelColor: '#000000',
                icon: 'userFriends',
                iconColor: 'grey',
                fieldName: '',
                defaultValue: '80',
                backgroundColor: '#FFE2BB',
                datasource: null
            },
            {
                label: 'Min Ocuupancy',
                labelColor: '#000000',
                icon: 'userFriends',
                iconColor: 'grey',
                fieldName: '',
                defaultValue: '45',
                backgroundColor: '#FFC0CB',
                datasource: null
            },
        ],

    }

    const [loading, setLoading] = React.useState(false)
    const [contextData, setContextData] = React.useState<IContextData>({})
    const [fieldProps, setFieldProps] = React.useState<IGridLayoutWidgetProps>(uiProps.fields || _.cloneDeep(defaultFieldProps));
    const [values, setValues] = React.useState<BoxesValues>([]);
    const [forceRenderCount, setForceRenderCount] = React.useState<number>(0);

    const { isLoading, initialContextData } = useInitialContextData(props, [])

    const commonProps = { ...defaultCommonFieldProps, ...uiProps.commonfields }
    const NumberOfRowsCount = Number(uiProps?.NumberOfRows?.text) || 2;
    const NumberOfColumnsCount = Number(uiProps?.NumberOfColumns?.text) || 1;

    const XPRProps: IUIPropertyDefinitionMap = {
        "container": ContainerProperty({
            background: {
                color: ""
            }
        }),
        'title': TextFieldProperty('Title', { text: 'Summary Indicators', backgroundColor: 'transparent', textColor: '#000000', fontSize: '1em' }, { isFontSizeHidden: true, isBackgroundColorHidden: true, useCustomColorPicker: false }),
        'NumberOfRows': NumberInput('Number of Rows', { text: '2' }, { minValue: 1, maxValue: 14 }),
        'NumberOfColumns': NumberInput('Number of Columns', { text: '1' }, { minValue: 1, maxValue: 14 }),
        'fields': GridLayoutWidgetProperty(fieldProps),
        'commonfields': GridLayoutCommonProps(commonProps),
        'channel': SimpleTextProperty('Channel', ''),
        'refreshInterval': SimpleTextProperty('Refresh Time', '5000'),
    }

    const title = uiProps?.title?.text || 'Summary Indicators';
    const titleColor = uiProps?.title?.textColor || '#000000';
    const visibleBoxCount = NumberOfRowsCount * NumberOfColumnsCount
    const { styles: containerStyles } = extractContainerProperties(uiProps.container);

    const refreshInterval = Number(uiProps?.refreshInterval) || 2000;
    const channel = uiProps?.channel;

    const isInDesignerMode = () => props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')

    React.useImperativeHandle(ref, () => ({
        beforeSave: validateBeforeSave,
        onPreview: validateBeforeSave
    }))

    React.useEffect(() => {
        setFieldProps(prev => ({ ...prev, boxProps: generateBoxProps() }))
    }, [NumberOfRowsCount, NumberOfColumnsCount]);

    React.useEffect(() => {
        processInitialSource(props.designer, props.uxpContext, props.uiProps, props.initialSource, 'dictionary')
            .then(res => {
                console.log('process initial source')
                // set the data source of first box to initial source
                fieldProps.boxProps[0] = { ...fieldProps?.boxProps[0], datasource: res.source }

                props?.designer?.setUIProps(Object.assign({}, uiProps, {
                    datasources: res?.datasources,
                    fields: { ...fieldProps, boxProps: fieldProps.boxProps }
                }))
            })
            .catch(e => {
                console.log('exception ', e);
            })

    }, [props.initialSource]);

    React.useEffect(() => {
        if (!isLoading) {
            setContextData(initialContextData);
        }
        setLoading(isLoading);
    }, [isLoading])

    React.useEffect(() => {
        validateBeforeSave()
    }, [uiProps])

    React.useEffect(() => {
        const elements = document.querySelectorAll('.item');
        elements.forEach((element) => {
            const borderColor = commonProps.borderColor;
            const pseudoStyles = `
              .item::after,.item::before {
                background-color: ${borderColor};
              }
            `;
            const styleSheet = document.createElement("style");
            styleSheet.innerText = pseudoStyles;
            document.head.appendChild(styleSheet);
        });

    }, [commonProps.borderColor]);

    useEffectWithPolling(props.uxpContext, channel || "values", refreshInterval || 6000, async () => {
        if (isInDesignerMode()) return;

        await Promise.allSettled(fieldProps?.boxProps?.map((box: IBoxProps, i: number) => {
            return executeAndBind(props.uxpContext, box?.datasource, uiProps.datasources, {}, contextData)
                .then(res => {
                    console.log('all settled code initial source')
                    if (res) {
                        values[i] = res
                        console.log('values[i]', values[i])
                        console.log('res', res)
                        setValues(values)
                        setForceRenderCount((c) => c + 1)
                    }
                })
        }))
    }, [props.designer]);

    function validateBeforeSave() {
        return validaSourcesAndFields(true);
    }

    function validaSourcesAndFields(showError?: boolean) {
        let hasErrors = false;

        for (let i = 0; i < visibleBoxCount; i++) {
            const errors = [];

            // clear errors before validation
            props.designer?.removeErrors?.([
                `field-no-data-source-${i}`,
                `no-field-name-${i}`
            ]);

            if (!hasValue(fieldProps?.boxProps[i]?.datasource?.source)) {
                errors.push({
                    id: `field-no-data-source-${i}`,
                    type: 'property' as any,
                    error: 'Each box must contain a data source',
                    containerId: `field-${i}`,
                    fieldId: `field-data-source-${i}`
                });
            }
            else if (!hasValue(fieldProps?.boxProps[i]?.fieldName)) {
                errors.push({
                    id: `no-field-name-${i}`,
                    type: 'property' as any,
                    error: 'Field name is required',
                    containerId: `field-${i}`,
                    fieldId: `field-name-${i}`
                });
            }

            if (errors.length && showError) {
                props.designer?.addErrors?.(errors);
                hasErrors = true;
                break
            }
        }

        return !hasErrors;
    }


    function generateBoxProps() {
        const itemCount = NumberOfRowsCount * NumberOfColumnsCount;
        const existingProps = fieldProps?.boxProps || [];

        if (existingProps.length > itemCount) {
            return existingProps.slice(0, itemCount);
        }

        return existingProps.concat(
            Array.from({ length: itemCount - existingProps.length }, (item, index) => {
                const isEven = (existingProps.length + index) % 2 === 0;
                return _.cloneDeep(defaultFieldProps.boxProps[isEven ? 0 : 1])
            })
        );
    }

    function getFieldValue(fieldName: string, boxIndex: number, boxProp?: IBoxProps) {

        boxProp = boxProp || uiProps.fields?.boxProps.find((b: IBoxProps) => b.fieldName === fieldName)
        if (isInDesignerMode() && boxProp.defaultValue) {
            return formatNumber(boxProp.defaultValue);
        }

        if (values[boxIndex]?.[fieldName]) {
            return formatNumber(values[boxIndex]?.[fieldName]);
        }

        return 0
    }

    return (
        <WidgetWrapper className='gridlayout-widget-template wd-widget-wrapper'>
            <div className="bg" style={containerStyles}></div>
            <SelectableItem
                style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}
                designer={props.designer}
                props={selectUIProps(uiProps, XPRProps, 'container', 'source', 'channel', 'refreshInterval')}
            >
                <div className='container'>
                    <div className='title-bar-wrapper'>
                        <SelectableItem
                            designer={props.designer}
                            props={selectUIProps(uiProps, XPRProps, 'title')}
                        >
                            <div className="uxp-widget-title-bar uxp-drag-handler">
                                <div className="title-section">
                                    <span className="txt" style={{ color: titleColor }}>{title}</span>
                                </div>
                                <div className="action-section">
                                    <div className='filter-symbol'>
                                        <FilterPanel />
                                    </div>
                                </div>
                            </div>
                        </SelectableItem>
                    </div>

                    <div className="data-area-container-wrapper">
                        <SelectableItem
                            designer={props.designer}
                            props={selectUIProps(uiProps, XPRProps, 'container', 'NumberOfRows', 'NumberOfColumns', 'commonfields')}
                        >
                            <div className="data-area-container">
                                <div
                                    className={'data-area'}
                                    style={{
                                        gridTemplateColumns: `repeat(${NumberOfColumnsCount}, 1fr)`,
                                        gridTemplateRows: `repeat(${NumberOfRowsCount}, 1fr)`,
                                    }}
                                >
                                    {
                                        fieldProps.boxProps ? (fieldProps.boxProps as IBoxProps[]).slice(0, visibleBoxCount).map((boxProp, i) => (
                                            <>
                                                <div className={"item-wrapper"} key={i}>
                                                    <SelectableItem
                                                        designer={props.designer}
                                                        props={selectUIProps(uiProps, XPRProps, 'fields')}
                                                        contextData={{ selectedIndex: i, selectedPart: 'all' }}
                                                        elementId={'field-' + i}
                                                    >
                                                        <div className={"item " + commonProps.orientation + (commonProps.showBorder ? ' show-border' : '')} style={{ backgroundColor: boxProp?.backgroundColor }}>
                                                            <div className='icon-wrapper'>
                                                                <SelectableItem
                                                                    designer={props.designer}
                                                                    props={selectUIProps(uiProps, XPRProps, 'fields')}
                                                                    contextData={{ selectedIndex: i, selectedPart: 'icon' }}
                                                                >
                                                                    <div className='icon-container'>
                                                                        <div className="icon-wrapper">
                                                                            <FontAwesomeIcon className="icon" color={boxProp?.iconColor} icon={generateIconProp(boxProp.icon)} />
                                                                        </div>
                                                                    </div>
                                                                </SelectableItem>
                                                            </div>

                                                            <div className='content'>
                                                                <div className='metric-wrapper'>
                                                                    <SelectableItem
                                                                        designer={props.designer}
                                                                        props={selectUIProps(uiProps, XPRProps, 'fields')}
                                                                        contextData={{ selectedIndex: i, selectedPart: 'value' }}
                                                                    >
                                                                        <div className={'metric'} style={{ color: boxProp?.labelColor }}>
                                                                            {getFieldValue(boxProp?.fieldName, i, boxProp)}
                                                                        </div>
                                                                    </SelectableItem>
                                                                </div>

                                                                <div className='label-wrapper'>
                                                                    <SelectableItem
                                                                        designer={props.designer}
                                                                        props={selectUIProps(uiProps, XPRProps, 'fields')}
                                                                        contextData={{ selectedIndex: i, selectedPart: 'label' }}
                                                                    >
                                                                        <div className='label' style={{ color: boxProp?.labelColor }}>
                                                                            {boxProp?.label}
                                                                        </div>
                                                                    </SelectableItem>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </SelectableItem>
                                                </div>
                                            </>
                                        )) : <></>
                                    }
                                </div>
                            </div>
                        </SelectableItem>
                    </div>


                </div>
            </SelectableItem>
        </WidgetWrapper>
    )
})

registerCardTemplate({
    id: 'gridlayout-widget',
    name: 'Grid Layout Widget',
    description: 'This widget can be used to represent multiple boxes.',
    template: XPR,
    icon: generateIconProp('boxes'),
    complexity: 'easy',
});